
// application/problem+json (API spec §7) surfaced as a typed error: `code` is the
// machine-matchable discriminant, `errors` the per-field detail of 422s.

export interface FieldError {
    field: string;
    message: string;
}



// E2E-2c rilievo 86 (2026-11-01): the holder of the per-datapool METADATA
// LEASE, as the 409 `metadata_lease_held` names it (backend http/problem.ts
// `problems.metadataLeaseHeld`, tasks/catalog.ts; contract Problem.leaseHolder).
// `family` is the holder's parent when the holding row is a step of a family —
// the lease belongs to the family, so that is what a client waits for; `task`
// is the row itself.
export interface LeaseHolder {
    task: string;
    family: string | null;
    type: string;
}



function fieldOf(value: object, key: string): unknown {
    return key in value ? Reflect.get(value, key) : undefined;
}



// The holder out of a problem body — null for every other problem (and for a
// `metadata_lease_held` that names no task, which the backend never renders).
// Pure, so the facade's retry middleware, the CLI and the webui read ONE shape.
export function leaseHolderOf(body: unknown): LeaseHolder | null {
    if (typeof body !== "object" || body === null || fieldOf(body, "code") !== "metadata_lease_held") {
        return null;
    }
    const holder = fieldOf(body, "leaseHolder");
    if (typeof holder !== "object" || holder === null) {
        return null;
    }
    const task = fieldOf(holder, "task");
    const family = fieldOf(holder, "family");
    const type = fieldOf(holder, "type");
    if (typeof task !== "string" || typeof type !== "string") {
        return null;
    }
    return { task, family: typeof family === "string" ? family : null, type };
}



// `Retry-After` in seconds (the delay form; an HTTP-date is not expected from
// this backend), null when absent or unreadable.
export function retryAfterSecondsOf(headers: Headers | Record<string, string> | undefined): number | null {
    if (headers === undefined) {
        return null;
    }
    const raw = headers instanceof Headers
        ? headers.get("retry-after")
        : (Object.entries(headers).find(([name]) => name.toLowerCase() === "retry-after")?.[1] ?? null);
    if (raw === null) {
        return null;
    }
    const seconds = Number(raw);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}



export class TVStorApiError extends Error {

    readonly status: number;
    readonly code: string;
    readonly detail: string | undefined;
    readonly errors: FieldError[];
    // E2E-2c rilievo 86 (2026-11-01): the response headers' Retry-After (any
    // problem that carries it: rate_limited, node_unreachable, the lease 409)
    // and the metadata-lease holder of a `metadata_lease_held` — null otherwise.
    readonly retryAfterSeconds: number | null;
    readonly leaseHolder: LeaseHolder | null;



    constructor(status: number, body: Record<string, unknown>, headers?: Headers | Record<string, string>) {
        const detail = typeof body["detail"] === "string" ? body["detail"] : undefined;
        const title = typeof body["title"] === "string" ? body["title"] : `HTTP ${status}`;
        // The per-field detail of 422s rides the message too: a bare "Validation
        // failed" helps nobody at a CLI prompt or in a webui alert (lesson inherited
        // from tvirt-v6, 2026-07-13: an admission refusal surfacing detail-lessly).
        const fields = Array.isArray(body["errors"])
            ? (body["errors"] as FieldError[]).map((e) => `${e.field}: ${e.message}`).join("; ")
            : "";
        super(`${title}${detail !== undefined ? `: ${detail}` : ""}${fields !== "" ? ` — ${fields}` : ""}`);
        this.name = "TVStorApiError";
        this.status = status;
        this.code = typeof body["code"] === "string" ? body["code"] : "unknown";
        this.detail = detail;
        this.errors = Array.isArray(body["errors"]) ? (body["errors"] as FieldError[]) : [];
        this.retryAfterSeconds = retryAfterSecondsOf(headers);
        this.leaseHolder = leaseHolderOf(body);
    }

}



// THE WAIT EXPIRED — THE TASK DID NOT. A separate class from TVStorTaskError on
// purpose (#102 CLI-WAIT, ratifica Luca 2026-08-12 §8): «ci sta mettendo troppo»
// and «e' fallito» are different facts about the world, and until this existed
// they arrived at the caller as the same bare `Error`, so the CLI could only
// spell them both `exit 1`. A script that reads exit codes could not tell a
// backup that failed from a backup that is still shipping. The CLI now maps
// this one — and only this one — to exit 124, the `timeout(1)` convention.
//
// `lastTask` is the last render the poll actually saw, so the caller can say
// what the task was doing when the wait gave up instead of only that it did.
export class TVStorTaskTimeoutError extends Error {

    readonly ref: string;
    readonly waitedMs: number;
    readonly lastTask: Record<string, unknown> | null;



    constructor(ref: string, waitedMs: number, lastTask: Record<string, unknown> | null) {
        const phase = lastTask !== null && typeof lastTask["phase"] === "string" ? lastTask["phase"] : "unknown";
        super(`timed out waiting for ${ref} after ${String(Math.round(waitedMs / 1000))}s — the WAIT expired, not the task, which was last seen ${phase}`);
        this.name = "TVStorTaskTimeoutError";
        this.ref = ref;
        this.waitedMs = waitedMs;
        this.lastTask = lastTask;
    }

}



// A task reached a terminal phase other than succeeded.
export class TVStorTaskError extends Error {

    readonly task: Record<string, unknown>;



    constructor(task: Record<string, unknown>) {
        const error = task["error"] as { title?: string; detail?: string } | undefined;
        const cause = error?.title !== undefined
            ? `: ${error.title}${error.detail !== undefined ? ` — ${error.detail}` : ""}`
            : "";
        super(`task ${String(task["id"])} ${String(task["phase"])}${cause}`);
        this.name = "TVStorTaskError";
        this.task = task;
    }

}

